<template>
  <div class="list-container">
    <div class="d-flex justify-content-between align-items-center">
      <h1 class="page-title">顧客毎の差引残高の一覧</h1>
      <div>
        <router-link :to="{ name: 'Home' }"> <!-- 業者情報詳細ページへのリンク -->
          <button type="button" class="btn btn-primary mx-1">ホーム</button>
        </router-link>
        <button @click="exportToExcel" class="btn btn-success">Excel出力</button>
      </div>
    </div>
    
    <div class="table-wrapper">
      <table class="table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">顧客名</th>
            <th class="text-nowrap">年間予定作業差引残高</th>
            <th class="text-nowrap">臨時作業差引残高</th>
            <th class="text-nowrap">合計差引残高</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customerBalance in customerBalances" :key="customerBalance.id">
            <td class="text-nowrap">{{ customerBalance.customer_nm }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerBalance.annual_work_balance) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerBalance.temporary_work_balance) }}</td>
            <td class="text-nowrap">{{ formatCurrency(customerBalance.total_balance) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect } from 'vue';
import * as XLSX from 'xlsx'; // SheetJSをインポート
import { getCustomerBalances } from '@/api/axios';

export default {
  setup() {
    const title = ref('顧客毎の差引残高');

    watchEffect(() => {
      document.title = title.value;
    });

    const customerBalances = ref([]);

    const fetchCustomerBalances = async () => {
      // 作成済みの当月の臨時作業の請求書を取得
      try {
        const response = await getCustomerBalances();
        customerBalances.value = response.data;
      } catch (error) {
        console.error('顧客毎の差引残高データの取得に失敗しました:', error);
      }
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;  // 日付部分だけを取得
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // Excel出力機能
    const exportToExcel = () => {
      // 現在の日時を取得して "yyyymmddHHMM" 形式にフォーマット
      const now = new Date();
      const formattedDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

      const data = customerBalances.value.map((balance) => ({
        顧客名: balance.customer_nm,
        年間予定作業差引残高: formatCurrency(balance.annual_work_balance),
        臨時作業差引残高: formatCurrency(balance.temporary_work_balance),
        合計差引残高: formatCurrency(balance.total_balance),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '顧客差引残高');

      // ファイル名を "顧客差引残高_yyyymmddHHMM.xlsx" にする
      const filename = `顧客差引残高_${formattedDate}.xlsx`;

      XLSX.writeFile(workbook, filename);
    };


    onMounted(fetchCustomerBalances);

    return {
      title,
      customerBalances,
      formatYearMonthDay,
      formatCurrency,
      exportToExcel, // メソッドを返す
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px;
  margin: auto;
}

.building-item {
  border: 1px solid #ddd;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.building-item:hover {
  background-color: #f5f5f5;
}

/* テーブルラッパーのスタイル */
.table-wrapper {
  max-height: calc(100vh - 270px); /* テーブルの高さを指定して、縦スクロールを発生させる */
  overflow-y: auto;  /* 縦スクロールを許可 */
  background: #fff;
  border: 1px solid #BBBBBB;
}

.table-wrapper table{
  background: #FFFFFF;
  border: 1px solid #BBBBBB;
  margin-bottom: 0;
}

.table-wrapper thead th {
  position: sticky;
  top: 0;
  background: #003366 !important; /* 背景色を設定し、スクロール中に背景が透けるのを防ぐ */
  color: #fff !important;
  z-index: 10;
}

.table-wrapper .th-sticky{
  z-index: 100 !important;
}

/* 横スクロール時に固定するカラム */
.table-wrapper th.sticky-left, td.sticky-left {
  position: sticky;
  left: 0;
  min-width: 64px;
  width: 64px;
  max-width: 64px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
  z-index: 50;
}

.table-wrapper th.sticky-left-second, td.sticky-left-second {
  position: sticky;
  left: 64px;
  min-width: 120px;
  width: 120px;
  max-width: 120px;
  background: #f0f8ff;
  box-shadow: inset -1px 0 #BBBBBB; /* 内側にボーダー風の影をつける */
  z-index: 49;
}

table th,
table td{
  font-size: 14px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.pagination-container button {
  margin: 0 10px;
}

.btn-cell{
  width: 184px;
}

.status-no-billing td {
  background-color: #FFD5EC !important;
}

.status-no-sending td {
  background-color: #FFDBC9 !important;
}

.status-no-schedule td {
  background-color: #CCFFCC !important;
}

.status-no-confirm td {
  background-color: #FFFFCC !important;
}

.status-all-ok td {
  background-color: #E6FFE9 !important;
}
</style>
